import React, { useEffect, useRef } from "react";
import {
  CreateCampaignsInputsContainer,
  TabViewWrapper,
} from "@client/pages/CreateCampaign/CreateCampaign.styled";
import { AgeLimitSelect } from "@common/components/Select/AgeLimitSelect";
import { useStores } from "@client/store";
import { Button, Flex, Spacer, Typography, useTheme } from "@ugc-ui/lego";
import { useIsMobile } from "@common/hooks/isMobile";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Path } from "@common/consts/Path";
import { usePageLock } from "@common/hooks/usePageLock";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { schema } from "@common/components/CreateUpdateInputs/validation";
import { FormValues } from "@common/components/CreateUpdateInputs/types";
import { TechnicalNameInput } from "@common/components/CreateUpdateInputs/components/TechnicalNameInput";
import { CompanyNameInput } from "@common/components/CreateUpdateInputs/components/CompanyNameInput";
import { DescriptionInput } from "@common/components/CreateUpdateInputs/components/DescriptionInput";
import { LinkInput } from "@common/components/CreateUpdateInputs/components/LinkInput";
import { ViewLimitInput } from "@common/components/CreateUpdateInputs/components/ViewLimitInput";
import { EndDateInput } from "@common/components/CreateUpdateInputs/components/EndDateInput";
import { DeviceTypeInput } from "@common/components/CreateUpdateInputs/components/DeviceTypeInput";
import { CountryIsoInput } from "@common/components/CreateUpdateInputs/components/CountryIsoInput";
import { SexSelectInput } from "@common/components/CreateUpdateInputs/components/SexSelectInput";
import { InterestsInput } from "@common/components/CreateUpdateInputs/components/InterestsInput";
import { UploadArea } from "@common/components/CreateUpdateInputs/components/UploadArea";
import { defaultValues } from "@common/components/CreateUpdateInputs/defaultValues";

export const CreateCampaignTabView = observer(() => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    client: {
      createCampaign: {
        handleSaveCampaign,
        handleStartCampaign,
        isSuccess,
        handleValidationError,
      },
      uploadAreaStore: {
        video,
        isVideoLoading,
        loadingProgress,
        handleUploadFile,
        detach: uploadAreaDetach,
      },
    },
    interestStore,
    countryStore,
    exitModalStore,
    videoPlayerModal,
    deviceTypeStore,
    ageLimitStore,
  } = useStores();

  const saveClicked = useRef(false);
  const startClicked = useRef(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    unregister,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      ...defaultValues,
      advertiser: {
        label: "1",
        value: "1",
      },
      legalCompanyName: "1",
    },
    resolver: joiResolver(schema()),
  });

  usePageLock();

  useEffect(() => {
    if (isSuccess) {
      navigate(Path.TO_HOME);
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    uploadAreaDetach();
  }, [uploadAreaDetach]);

  useEffect(() => {
    unregister("isMultiSubscription");
  }, [unregister]);

  const resetButtonClicked = () => {
    saveClicked.current = false;
    startClicked.current = false;
  };

  const handleFormErrors = (validationErrors) => {
    resetButtonClicked();
    handleValidationError(validationErrors);
  };

  const handleFormSubmit = (fields: FormValues) => {
    if (saveClicked.current) {
      handleSaveCampaign(fields);
    }
    if (startClicked.current) {
      handleStartCampaign(fields);
    }

    resetButtonClicked();
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit, handleFormErrors)}>
      <TabViewWrapper>
        <Flex
          flexGrow={2}
          flexDirection="column"
          justifyContent="space-between"
        >
          <CreateCampaignsInputsContainer
            theme={theme}
            flexDirection="column"
            gap={24}
          >
            <TechnicalNameInput control={control} errors={errors} />

            <CompanyNameInput control={control} errors={errors} />

            <DescriptionInput control={control} errors={errors} />

            <LinkInput control={control} errors={errors} />

            <ViewLimitInput control={control} errors={errors} />

            <EndDateInput control={control} errors={errors} />

            <Typography type="subtitle2" color={theme.gray.a100}>
              Таргетинг
            </Typography>

            <Flex flexDirection="row" justifyContent="space-between" gap={24}>
              <DeviceTypeInput
                control={control}
                errors={errors}
                deviceTypeStore={deviceTypeStore}
              />

              <CountryIsoInput
                control={control}
                errors={errors}
                countryStore={countryStore}
              />
            </Flex>
            <Flex gap={24}>
              <SexSelectInput control={control} errors={errors} />

              <AgeLimitSelect
                control={control}
                errors={errors}
                theme={theme}
                ageLimitStore={ageLimitStore}
              />
            </Flex>
            <Flex>
              <InterestsInput
                control={control}
                errors={errors}
                interestStore={interestStore}
              />
            </Flex>
          </CreateCampaignsInputsContainer>

          <Flex justifyContent="space-evenly" gap={24}>
            <Button
              theme={theme}
              color="default"
              variant="outlined"
              dataCy="кнопка-сохранить"
              onClick={() => {
                saveClicked.current = true;
              }}
              fullWidth
              type="submit"
            >
              Сохранить
            </Button>
            <Button
              color="primary"
              theme={theme}
              onClick={() => {
                startClicked.current = true;
              }}
              dataCy="кнопка-запустить"
              fullWidth
              type="submit"
            >
              Запустить
            </Button>
          </Flex>
        </Flex>

        <Flex
          flexDirection="column"
          flexGrow={1}
          alignItems={!isMobile ? "end" : "start"}
        >
          {isMobile && (
            <>
              <Typography type="subtitle2" color={theme.gray.a40}>
                Черновик
              </Typography>
              <Spacer size={12} vertical />
            </>
          )}

          <UploadArea
            control={control}
            errors={errors}
            video={video}
            isVideoLoading={isVideoLoading}
            loadingProgress={loadingProgress}
            onUploadFile={handleUploadFile}
            uploadAreaDetach={uploadAreaDetach}
            campaignData={{
              name: watch("name"),
              description: watch("description"),
              link: watch("productLink"),
            }}
            exitModalStore={exitModalStore}
            videoPlayerModalStore={videoPlayerModal}
          />
        </Flex>
      </TabViewWrapper>
    </form>
  );
});
